import type { HTMLAttributes, ReactNode } from "react";
import { Copy, Pin, PinOff, Trash2 } from "lucide-react";
import { Icon, Sheet } from "@cntrl/ui";
import { REACTION_EMOJIS } from "../../app/hooks/inboxFormat.js";
import type { MessageActionsProps } from "./MessageActions.js";
import { useLongPress } from "./useLongPress.js";
import styles from "./Inbox.module.scss";

export interface MessageActionsSheetProps extends Omit<MessageActionsProps, "mine" | "onOpen"> {
  onClose: () => void;
  /** Copy the message's words (ART-270). The phone's only way to copy them, since bubbles are not selectable
   *  there; absent for a message with no words. */
  onCopy?: () => void;
}

/**
 * A message's actions on a PHONE (ART-264) — opened by a press-and-hold on the bubble, where a desktop hovers for
 * the `…` trigger (`MessageActions`). The same acts in the same order — react, pin, delete — plus Copy, which a
 * phone needs because its bubbles are not selectable (ART-270). A bottom sheet
 * rather than the desktop's floating row because a row of 26px emoji under a thumb is a row of mis-taps, and a
 * sheet is where a phone already puts "what do you want to do with this".
 *
 * Every action closes the sheet first, then acts — the same order the desktop menu uses, so a slow request never
 * leaves the sheet up over its own result.
 */
export function MessageActionsSheet({ onReact, onCopy, onPin, pinned, onDelete, onClose }: MessageActionsSheetProps) {
  const act = (fn: () => void) => () => {
    onClose();
    fn();
  };
  return (
    <Sheet title="Message actions" onClose={onClose} closeLabel="Close" backdropClassName={styles.actionsSheetBackdrop}>
      <div className={styles.actionsSheet}>
        <div className={styles.actionsSheetReactions} role="group" aria-label="React">
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={styles.actionsSheetEmoji}
              aria-label={`React ${emoji}`}
              onClick={act(() => onReact(emoji))}
            >
              {emoji}
            </button>
          ))}
        </div>
        {(onCopy || onPin || onDelete) && (
          <div className={styles.actionsSheetList}>
            {onCopy && (
              <button type="button" className={styles.actionsSheetItem} onClick={act(onCopy)}>
                <Icon icon={Copy} size={20} />
                Copy
              </button>
            )}
            {onPin && (
              <button type="button" className={styles.actionsSheetItem} onClick={act(onPin)}>
                <Icon icon={pinned ? PinOff : Pin} size={20} />
                {pinned ? "Unpin" : "Pin"}
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className={`${styles.actionsSheetItem} ${styles.actionsSheetDanger}`}
                onClick={act(onDelete)}
              >
                <Icon icon={Trash2} size={20} />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </Sheet>
  );
}

/**
 * A bubble that opens its actions on a press-and-hold (ART-264). A component rather than inline handlers because
 * the hold needs per-bubble state (its timer, its start point) and a thread renders bubbles in a loop, where a
 * hook cannot be called. `enabled` is false on desktop, where it renders a plain div and the `…` trigger serves.
 */
export function Holdable({
  enabled,
  onHold,
  onDoubleTap,
  children,
  ...rest
}: {
  enabled: boolean;
  onHold: () => void;
  /** A double-tap on the bubble (ART-264: hearts the message). Touch only, like the hold. */
  onDoubleTap?: () => void;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  const handlers = useLongPress(onHold, enabled, onDoubleTap);
  return (
    <div {...rest} {...handlers} data-hold={enabled || undefined}>
      {children}
    </div>
  );
}
